/** Polling rules, configured once rather than per screen:
 *
 *  - only the VISIBLE screen polls (TanStack Query stops a query with no mounted observer)
 *  - polling pauses when the tab is hidden, and resumes immediately on return rather than
 *    waiting out the interval
 *  - failures back off exponentially, capped, and recover to the normal interval on the first
 *    success
 *  - a poller that backs off SAYS SO in the console. A UI that silently stops updating is the
 *    hardest kind of bug for a user to report.
 */
import { QueryClient } from "@tanstack/react-query";

const MAX_BACKOFF_MS = 60_000;

/** Only the parts of a query this needs. Accepting a structural supertype lets one helper serve
 * every differently-typed query without generics at each call site. */
interface PollableQuery {
  queryKey: readonly unknown[];
  state: { fetchFailureCount: number; error: unknown };
}

export function refetchIntervalFor(baseMs: number) {
  return (query: PollableQuery): number | false => {
    if (typeof document !== "undefined" && document.hidden) return false;

    const failures = query.state.fetchFailureCount;
    if (failures === 0) return baseMs;

    const backoff = Math.min(baseMs * 2 ** failures, MAX_BACKOFF_MS);
    console.error(
      `poller '${String(query.queryKey[0])}' has failed ${failures} time(s); backing off to ${backoff}ms`,
      query.state.error,
    );
    return backoff;
  };
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: 2_000,
        refetchOnWindowFocus: true,
        // Resumes immediately on return to the tab rather than waiting out the interval.
        refetchOnReconnect: true,
      },
    },
  });
}

/** The doors list refreshes every 10 seconds: a door that drops into an unexpected override
 * must appear without anyone pressing anything. */
export const DOORS_POLL_MS = 10_000;
export const STATUS_POLL_MS = 15_000;
export const WATCHDOG_POLL_MS = 15_000;
